import { useMemo, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";

import DonatePageUI from "../../../ui/dashboard/donate/DonatePageUI";
import { useDarkModeCheck } from "../DashboardLayout";
import { SEND_DONATION_FORM } from "../../../service/graphql/mutation";

const DonatePage = () => {
  const navigate = useNavigate();
  const { darkMode } = useDarkModeCheck();

  const [nameOfBuilding, setNameOfBuilding] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [pickupAddress, setPickupAddress] = useState("");
  const [temporaryParkingSpace, setTemporaryParkingSpace] = useState("");
  const [postcode, setPostcode] = useState("");
  const [availableDateFrom, setAvailableDateFrom] = useState<Date | null>(null);
  const [availableDateTo, setAvailableDateTo] = useState<Date | null>(null);
  const [itemsTable, setItemsTable] = useState<string>("");
  const [itemsAvailable, setItemsAvailable] = useState<string>("");
  const [attachments, setAttachments] = useState<File[]>([]);

  const [submissionInProgress, setSubmissionInProgress] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitMsg, setSubmitMsg] = useState<string>();

  const [invalidNameOfBuilding, setInvalidNameOfBuilding] = useState<string>();
  const [invalidContactName, setInvalidContactName] = useState<string>();
  const [invalidEmail, setInvalidEmail] = useState<string>();
  const [invalidPickupAddress, setInvalidPickupAddress] = useState<string>();
  const [invalidTemporaryParkingSpace, setInvalidTemporaryParkingSpace] = useState<string>();
  const [invalidPostcode, setInvalidPostcode] = useState<string>();
  const [invalidAvailableDateFrom, setInvalidAvailableDateFrom] = useState<string>();
  const [invalidAvailableDateTo, setInvalidAvailableDateTo] = useState<string>();
  const [invalidItemsTable, setInvalidItemsTable] = useState<string>();
  const [invalidItemsAvailable, setInvalidItemsAvailable] = useState<string>();
  const [invalidAttachments, setInvalidAttachments] = useState<string>();

  const [sendDonationEmail, { data: mailRes }] = useMutation(
    SEND_DONATION_FORM,
    {
      onError: (err: any) => {
        setSubmissionInProgress(false);
        setIsSuccess(false);
        setSubmitMsg(err.message);
      },
      errorPolicy: "all",
    }
  );

  const handleFileChange = useCallback(
    (files: File[]) => {
      const MAX_FILES = 30;
      const MAX_TOTAL_ATTACHMENT_BYTES = 30 * 1024 * 1024; // 30 MB

      const validTypes = [
        "image/jpeg",
        "image/png",
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];

      if (files.length > MAX_FILES) {
        setInvalidAttachments(`You can only upload up to ${MAX_FILES} files`);
        return;
      }

      let totalBytes = 0;
      const tempFileList: File[] = [];

      for (const file of files) {
        if (!validTypes.includes(file.type)) {
          setInvalidAttachments(`Invalid file type: ${file.name}`);
          return;
        }

        totalBytes += file.size;

        if (totalBytes > MAX_TOTAL_ATTACHMENT_BYTES) {
          setInvalidAttachments(
            "Total attachment size cannot exceed total 30 MB"
          );
          return;
        }

        tempFileList.push(file);
      }

      setAttachments(tempFileList);
    },
    [setAttachments, setInvalidAttachments]
  );

  const resetInvalidMsg = useCallback(() => {
    setInvalidNameOfBuilding(undefined);
    setInvalidContactName(undefined);
    setInvalidEmail(undefined);
    setInvalidPickupAddress(undefined);
    setInvalidTemporaryParkingSpace(undefined);
    setInvalidPostcode(undefined);
    setInvalidAvailableDateFrom(undefined);
    setInvalidAvailableDateTo(undefined);
    setInvalidItemsTable(undefined);
    setInvalidItemsAvailable(undefined);
    setInvalidAttachments(undefined);
    setSubmitMsg(undefined);
    setIsSuccess(false);
  }, [setInvalidAttachments, setInvalidItemsTable]);

  const padTo2Digits = (num: number) => {
    return num.toString().padStart(2, "0");
  };

  const formatDate = useCallback((date: Date) => {
    return [
      date.getFullYear(),
      padTo2Digits(date.getMonth() + 1),
      padTo2Digits(date.getDate()),
    ].join("-");
  }, []);

  const submitForm = useCallback(() => {
    resetInvalidMsg();
    setSubmissionInProgress(true);

    // Validation section
    let isValid = true;
    if (nameOfBuilding.length <= 0) {
      setInvalidNameOfBuilding("Submitter name is required");
      isValid = false;
    }
    if (contactName.length <= 0) {
      setInvalidContactName("Contact name is required");
      isValid = false;
    }
    if (email.length <= 0) {
      setInvalidEmail("Email is required");
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setInvalidEmail("Invalid email format");
      isValid = false;
    }
    if (pickupAddress.length <= 0) {
      setInvalidPickupAddress("Pickup address is required");
      isValid = false;
    }
    if (temporaryParkingSpace.length <= 0) {
      setInvalidTemporaryParkingSpace("Temporary parking space information is required");
      isValid = false;
    }
    if (postcode.length <= 0) {
      setInvalidPostcode("Postcode is required");
      isValid = false;
    } else if (!/^([A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})$/i.test(postcode)) {
      setInvalidPostcode("Invalid postcode format");
      isValid = false;
    }
    if (itemsAvailable.length <= 0) {
      setInvalidItemsAvailable("Items available is required");
      isValid = false;
    }
    if (itemsTable.length <= 0) {
      setInvalidItemsTable("Item is required");
      isValid = false;
    }
    if (attachments.length === 0) {
      setInvalidAttachments("At least one attachment is required");
      isValid = false;
    }
    if (!availableDateFrom) {
      setInvalidAvailableDateFrom("Available date is required");
      isValid = false;
    } else if (availableDateFrom <= new Date()) {
      setInvalidAvailableDateFrom("Available date accepts future dates only");
      isValid = false;
    }
    if (!availableDateTo) {
      setInvalidAvailableDateTo("Available date to is required");
      isValid = false;
    } else if (availableDateTo <= new Date()) {
      setInvalidAvailableDateTo("Available date to accepts future dates only");
      isValid = false;
    }
    if (!isValid) {
      setSubmissionInProgress(false);
      return;
    }

    // Simpler way to handle attachments - uncomment if you don't need compression:
    // Convert File objects to base64 strings before sending
    // const filePromises = attachments.map((file) => {
    //   return new Promise<any>((resolve, reject) => {
    //     const reader = new FileReader();
    //     reader.onload = () => {
    //       if (typeof reader.result === "string") {
    //         // Get just the base64 part without the prefix
    //         const base64String = reader.result.split(",")[1];
    //         resolve({
    //           name: file.name,
    //           type: file.type,
    //           size: file.size,
    //           base64: base64String,
    //         });
    //       } else {
    //         reject(new Error("Failed to convert file to base64"));
    //       }
    //     };
    //     reader.onerror = () => {
    //       reject(new Error("Failed to read file"));
    //     };
    //     reader.readAsDataURL(file);
    //   });
    // });

    const MAX_FILE_SIZE = 300 * 1024; // 300 KB - more aggressive compression

    const convertFileToBase64WithCompression = (file: File): Promise<any> => {
    const isCompressibleFormat = file.type === "image/jpeg" || file.type === "image/png";

    if (!isCompressibleFormat) {
        // For other formats, convert to base64 directly, no compression
        return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            if (typeof reader.result === "string") {
            const base64String = reader.result.split(",")[1];
            resolve({
                name: file.name,
                type: file.type,
                size: file.size,
                base64: base64String,
            });
            } else {
            reject(new Error("Failed to convert file to base64"));
            }
        };
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(file);
        });
    }

    return new Promise((resolve, reject) => {
        const img = new Image();
        const reader = new FileReader();

        reader.onload = (e) => {
        if (typeof e.target?.result !== "string") {
            reject(new Error("Failed to read file"));
            return;
        }
        img.src = e.target.result;
        };

        reader.onerror = () => reject(new Error("Failed to read file"));

        img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) {
            reject(new Error("Failed to get canvas context"));
            return;
        }

        // Resize image if too large (max 1920px on longest side)
        const maxDimension = 1920;
        let width = img.width;
        let height = img.height;
        
        if (width > maxDimension || height > maxDimension) {
            if (width > height) {
                height = Math.round((height * maxDimension) / width);
                width = maxDimension;
            } else {
                width = Math.round((width * maxDimension) / height);
                height = maxDimension;
            }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        let quality = 0.8; // Start with lower quality
        let base64String = "";
        let blobSize = Infinity;

        while (quality > 0.1) {
            base64String = canvas.toDataURL(file.type, quality);
            blobSize = Math.ceil((base64String.length - base64String.indexOf(",") - 1) * 3 / 4);

            if (blobSize <= MAX_FILE_SIZE) break;

            quality -= 0.05; // Smaller steps for finer control
        }

        if (blobSize > MAX_FILE_SIZE) {
            return reject(new Error("Unable to compress image below 300KB"));
        }

        const cleanBase64 = base64String.split(",")[1];

        resolve({
            name: file.name,
            type: file.type,
            size: blobSize,
            base64: cleanBase64,
        });
        };

        img.onerror = () => reject(new Error("Could not load image"));

        reader.readAsDataURL(file);
    });
    };

    const filePromises = attachments.map(convertFileToBase64WithCompression);

    Promise.all(filePromises)
      .then((fileData) => {
        sendDonationEmail({
          variables: {
            data: {
              nameOfBuilding,
              contactName,
              emailFrom: email,
              pickupAddress,
              temporaryParkingSpace,
              postcode,
              pickupDateFrom: formatDate(availableDateFrom ?? new Date()),
              pickupDateTo: formatDate(availableDateTo ?? new Date()),
              itemsTable,
              itemsAvailable,
              attachments: fileData,
            },
          },
          context: {
            apiName: "cms",
          },
        });
      })
      .catch((error) => {
        setSubmissionInProgress(false);
        setIsSuccess(false);
        setSubmitMsg(error.message || "Failed to process attachments");
      });
  }, [
    nameOfBuilding,
    contactName,
    email,
    pickupAddress,
    postcode,
    temporaryParkingSpace,
    itemsAvailable,
    itemsTable,
    availableDateFrom,
    availableDateTo,
    attachments,
    resetInvalidMsg,
    setInvalidNameOfBuilding,
    setInvalidContactName,
    setInvalidEmail,
    setInvalidPickupAddress,
    setInvalidPostcode,
    setInvalidTemporaryParkingSpace,
    setInvalidItemsTable,
    setInvalidItemsAvailable,
    setInvalidAvailableDateFrom,
    setInvalidAvailableDateTo,
    setSubmissionInProgress,
    formatDate,
    sendDonationEmail,
  ]);

  useEffect(() => {
    if (mailRes) {
      setSubmissionInProgress(false);
      if (mailRes.sendDonationEmail === "OK") {
        setIsSuccess(true);
        setSubmitMsg("Thank you! Your donation submission has been received.");
      } else {
        setIsSuccess(false);
        setSubmitMsg(
          mailRes.sendDonationEmail ||
            "An error occurred while sending your donation form."
        );
      }
    }
  }, [mailRes]);

  const onCancel = useCallback(() => {
    setNameOfBuilding("");
    setContactName("");
    setEmail("");
    setPickupAddress("");
    setTemporaryParkingSpace("");
    setPostcode("");
    setAvailableDateFrom(null);
    setAvailableDateTo(null);
    setItemsTable("");
    setItemsAvailable("");
    setAttachments([]);
    resetInvalidMsg();
    navigate("/dashboard/home");
  }, [navigate, resetInvalidMsg]);

  const donatePageProps = useMemo(
    () => ({
      nameOfBuilding,
      contactName,
      email,
      pickupAddress,
      temporaryParkingSpace,
      postcode,
      availableDateFrom,
      availableDateTo,
      itemsAvailable,
      itemsTable,
      attachments,
      invalidNameOfBuilding,
      invalidContactName,
      invalidEmail,
      invalidPickupAddress,
      invalidPostcode,
      invalidItemsTable,
      invalidItemsAvailable,
      invalidAvailableDateFrom,
      invalidAvailableDateTo,
      invalidAttachments,
      invalidTemporaryParkingSpace,
      submissionInProgress,
      isSuccess,
      submitMsg,
      setNameOfBuilding,
      setContactName,
      setEmail,
      setPickupAddress,
      setPostcode,
      setAvailableDateFrom,
      setAvailableDateTo,
      setItemsTable,
      setItemsAvailable,
      setTemporaryParkingSpace,
      handleFileChange,
      resetInvalidMsg,
      onSubmit: submitForm,
      onCancel,
      darkMode,
    }),
    [
      nameOfBuilding,
      contactName,
      email,
      pickupAddress,
      postcode,
      temporaryParkingSpace,
      availableDateFrom,
      availableDateTo,
      itemsTable,
      itemsAvailable,
      attachments,
      invalidNameOfBuilding,
      invalidContactName,
      invalidEmail,
      invalidPickupAddress,
      invalidPostcode,
      invalidItemsTable,
      invalidItemsAvailable,
      invalidAvailableDateFrom,
      invalidAvailableDateTo,
      invalidAttachments,
      invalidTemporaryParkingSpace,
      submissionInProgress,
      isSuccess,
      submitMsg,
      setNameOfBuilding,
      setContactName,
      setEmail,
      setPickupAddress,
      setPostcode,
      setAvailableDateFrom,
      setAvailableDateTo,
      setItemsTable,
      setItemsAvailable,
      setTemporaryParkingSpace,
      handleFileChange,
      resetInvalidMsg,
      submitForm,
      onCancel,
      darkMode,
    ]
  );

  return <DonatePageUI {...donatePageProps} />;
};

export default DonatePage;
